import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CCard, CCardGroup } from "@coreui/react";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import ServiceCategoryForm from "../serviceCategoryForm";
import { serviceCategory } from "@/constants/translation/serviceCategory.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import ServiceCategoryValidationClass from "@/helpers/validations/serviceCategory";
import { masters } from "@/libs/routes";
import { useGetServiceCategory, useUpdateServiceCategory } from "@/hooks/useServiceCategory";

/**
 * Component for editing an existing service category.
 * @returns {JSX.Element} The rendered EditServiceCategory component.
 */
const EditServiceCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useTranslationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: response, isLoading: isDataLoading } = useGetServiceCategory(id, undefined, () => {
    toast.error(t(messages.toast.serviceCategory.dataFetchFailed));
  });

  const { mutate: doUpdateServiceCategory } = useUpdateServiceCategory(
    (response) => {
      toast.success(response?.message || t(messages.toast.serviceCategory.updated));
      navigate(masters.serviceCategory.list);
    },
    (error) => {
      toast.error(error?.response?.data?.message || t(messages.toast.serviceCategory.updateFailed));
      setIsSubmitting(false);
    }
  );

  const handleUpdateServiceCategory = (values) => {
    setIsSubmitting(true);
    doUpdateServiceCategory({ uuid: id, name: values.name });
  };

  const serviceCategoryData = response?.data;

  const initialValues = useMemo(
    () => ({
      name: serviceCategoryData?.name || "",
    }),
    [serviceCategoryData]
  );

  const validationSchema = useMemo(() => ServiceCategoryValidationClass.serviceCategoryValidation(t), [t]);

  if (isDataLoading) {
    return (
      <CCardGroup>
        <CCard className="p-4">
          <div className="text-center">
            <p>{t(serviceCategory.edit.main_heading)}...</p>
          </div>
        </CCard>
      </CCardGroup>
    );
  }

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(serviceCategory.edit.main_heading)}</h1>
        <ServiceCategoryForm
          initialValues={initialValues}
          onSubmit={handleUpdateServiceCategory}
          isSubmitting={isSubmitting}
          validationSchema={validationSchema}
          isEdit
        />
      </CCard>
    </CCardGroup>
  );
};

EditServiceCategory.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.serviceCategory?.list,
    pathname: masters.serviceCategory.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.serviceCategory?.edit,
    pathname: "",
    active: true,
  },
];

export default EditServiceCategory;
